import { Moment } from 'moment';
import BaseModel from '@/shared/models/BaseModel';
import User from '@/shared/models/User';
import { Constructor } from '@/shared/utils/typescript';
import DiagnosticType, { COMMENT_DIAGNOSTIC_TYPES } from '../../constants/DiagnosticType';
import DiagnosticTypeDescription, { DiagnosticTypeDescriptionId } from './DiagnosticTypeDescription';

export interface DiagnosticId {__diagnosticId: number}

export default abstract class Diagnostic extends BaseModel<DiagnosticId> {
  typeDescriptionId!: DiagnosticTypeDescriptionId;
  description = '';
  hasTimer = false;
  hasComment = false;
  hasMediaEnabled = false;
  isMandatory = false;
  isAdditionalTime = false;
  isExportable = false;
  answeredBy: User | null = null;
  answeredOn: Moment | null = null;
  startedOn: Moment | null = null;
  stoppedOn: Moment | null = null;
  comment: string | null = null;
  isPrimary = false;

  constructor(
    readonly type: DiagnosticType,
  ) {
    super();
  }

  isComment(): boolean {
    return COMMENT_DIAGNOSTIC_TYPES.includes(this.type);
  }

  static getSubclass(type: DiagnosticType): Constructor<Diagnostic> {
    switch (type) {
      case DiagnosticType.COMMENT:
      case DiagnosticType.TEXT:
        return CommentDiagnostic;
      case DiagnosticType.COMPLAINT:
        return ComplaintDiagnostic;
      case DiagnosticType.OBSERVATION:
        return ObservationDiagnostic;
      case DiagnosticType.DIAGNOSIS:
        return DiagnosisDiagnostic;
      case DiagnosticType.ADVICE:
        return AdviceDiagnostic;
      case DiagnosticType.REPAIR:
        return RepairDiagnostic;
      case DiagnosticType.RESULT:
        return ResultDiagnostic;
      case DiagnosticType.PARTS_TO_STOCK:
        return PartsToStockDiagnostic;
      case DiagnosticType.REVIEW:
        return ReviewDiagnostic;
      case DiagnosticType.BOOLEAN:
        return BooleanDiagnostic;
    }
  }

  static forType<T extends Diagnostic>(type: DiagnosticType): T {
    const subclass = Diagnostic.getSubclass(type);
    return new subclass() as any; // eslint-disable-line new-cap
  }

  static fromTypeDescription(description: DiagnosticTypeDescription): Diagnostic {
    const result = Diagnostic.forType(description.type);
    result.typeDescriptionId = description.id;
    result.description = description.description;
    result.hasTimer = description.hasTimer;
    result.hasComment = description.hasComment;
    return result;
  }
}

export class CommentDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.COMMENT);
  }
}

export class ComplaintDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.COMPLAINT);
  }

  complaint: string | null = null;
  conditions: string | null = null;
}

export class ObservationDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.OBSERVATION);
  }

  isObserved: boolean | null = null;
}

export class DiagnosisDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.DIAGNOSIS);
  }

  expected: string | null = null;
  actual: string | null = null;
}

export class AdviceDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.ADVICE);
  }

  estimatedTime: Moment | null = null;
  isNeeded: boolean | null = null;
  mattersToInspection: boolean | null = null;
}

export class RepairDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.REPAIR);
  }
}

export class ResultDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.RESULT);
  }

  isResolved: boolean | null = null;
}

export class PartsToStockDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.PARTS_TO_STOCK);
  }

  isWarranty: boolean | null = null;
}

export class ReviewDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.REVIEW);
  }

  isComplete: boolean | null = null;
}

export class BooleanDiagnostic extends Diagnostic {
  constructor() {
    super(DiagnosticType.BOOLEAN);
  }

  isTrue: boolean | null = null;
}